// Verification Service for Twitter and Discord
// This service handles verification of social media actions

// Constants
const VERIFICATION_STORAGE_KEY = 'palm_heist_verification';
const VERIFICATION_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Verify Twitter engagement (follow and share)
 * @param {string} walletAddress - User's wallet address
 * @returns {Promise<Object>} - Verification result
 */
export const verifyTwitterEngagement = async (walletAddress) => {
  // In production, this would make an API call to your backend
  // The backend would then use Twitter API to verify the user's actions
  
  // Simulating API call with a delay
  return new Promise((resolve) => {
    setTimeout(() => {
      // Mock successful verification
      resolve({
        success: true,
        followed: true,
        shared: true,
        message: "Successfully verified Twitter engagement"
      });
    }, 1500);
  });
};

/**
 * Verify Discord role
 * @param {string} walletAddress - User's wallet address
 * @returns {Promise<Object>} - Verification result
 */
export const verifyDiscordRole = async (walletAddress) => {
  // In production, this would make an API call to your backend
  // The backend would then use Discord API to verify the user's membership and role
  
  // Simulating API call with a delay
  return new Promise((resolve) => {
    setTimeout(() => {
      // Mock successful verification
      resolve({
        success: true,
        joined: true,
        hasRole: true,
        message: "Successfully verified Discord role"
      });
    }, 1500);
  });
};

/**
 * Store verification status in localStorage
 * @param {Object} status - Verification status object
 */
export const storeVerificationStatus = (status) => {
  const verificationData = {
    timestamp: Date.now(),
    twitter: status.twitter,
    discord: status.discord
  };
  
  localStorage.setItem(VERIFICATION_STORAGE_KEY, JSON.stringify(verificationData));
};

/**
 * Get verification status from localStorage
 * @returns {Object|null} - Verification status or null if not found
 */
export const getVerificationStatus = () => {
  const storedData = localStorage.getItem(VERIFICATION_STORAGE_KEY);
  return storedData ? JSON.parse(storedData) : null;
};

/**
 * Check if verification is still valid (not expired)
 * @param {Object} status - Verification status object
 * @returns {boolean} - Whether verification is still valid
 */
export const isVerificationValid = (status) => {
  if (!status || !status.timestamp) return false;
  
  const now = Date.now();
  const elapsed = now - status.timestamp;
  
  return elapsed < VERIFICATION_EXPIRY;
};

/**
 * Check if user is fully verified
 * @returns {boolean} - Whether user is fully verified
 */
export const isFullyVerified = () => {
  const status = getVerificationStatus();
  
  if (!status || !isVerificationValid(status)) return false;
  
  return (
    status.twitter?.followed === true &&
    status.twitter?.shared === true &&
    status.discord?.joined === true &&
    status.discord?.hasRole === true
  );
};

export default {
  verifyTwitterEngagement,
  verifyDiscordRole,
  storeVerificationStatus,
  getVerificationStatus,
  isVerificationValid,
  isFullyVerified
};
